import {
  type GetUserRolesError,
  useGetUserRoles,
} from '@/generated/http-clients/v3ApiComponents'
import type { RoleList } from '@/generated/http-clients/v3ApiSchemas'
import type { UseQueryResult } from '@tanstack/react-query'
import { type PropsWithChildren, createContext, useContext } from 'react'

const MY_USER = 'me'

export enum RoleName {
  ADMIN = 'admin',
  READER = 'reader',
  OPERATOR = 'operator',
}

type RBACPermissionsContextType = {
  /**
   * User roles query result
   */
  userRoles: UseQueryResult<RoleList, GetUserRolesError>
  /**
   * Get the user's roles array, returns empty array if no roles available
   */
  getRoles: () => RoleList['roles']
  /**
   * Check if the current user has a specific role
   */
  hasRole: (roleName: RoleName) => boolean
  /**
   * Check if the current user has admin rights
   */
  hasAdminRights: () => boolean
  /**
   * Check if the current user has operator rights
   */
  hasOperatorRights: () => boolean
  /**
   * Check if the current user has reader rights
   */
  hasReaderRights: () => boolean
}

const RBACPermissionsContext = createContext<
  RBACPermissionsContextType | undefined
>(undefined)

export function useRBACPermissions() {
  const context = useContext(RBACPermissionsContext)
  if (!context) {
    throw new Error(
      'useRBACPermissions must be used within an RBACPermissionsProvider',
    )
  }
  return context
}

/**
 * Provider to manage role-based access control permissions
 *
 * Fetches user roles from the API and provides permission checking functions
 */
export function RBACPermissionsProvider({
  children,
}: Readonly<PropsWithChildren>) {
  // Fetch user roles for the current authenticated user
  const userRoles = useGetUserRoles({
    pathParams: {
      userId: MY_USER,
    },
  })

  /**
   * Get the user's roles array, returns empty array if no roles available
   */
  function getRoles(): RoleList['roles'] {
    const roles = (userRoles.data as RoleList)?.roles
    return roles || []
  }

  /**
   * Check if the current user has a specific role
   * @param roleName - The role to check for
   * @returns True if the user has the specified role
   */
  function hasRole(roleName: RoleName): boolean {
    const roles = getRoles()
    return roles?.some((role) => role.name === roleName) ?? false
  }

  /**
   * Check if the current user has admin rights
   * Returns true if one of the user's roles matches 'admin'
   */
  function hasAdminRights(): boolean {
    return hasRole(RoleName.ADMIN)
  }

  /**
   * Check if the current user has operator rights
   * Returns true if one of the user's roles matches 'operator'
   */
  function hasOperatorRights(): boolean {
    return hasRole(RoleName.OPERATOR)
  }

  /**
   * Check if the current user has reader rights
   * Returns true if one of the user's roles matches 'reader'
   */
  function hasReaderRights(): boolean {
    return hasRole(RoleName.READER)
  }

  return (
    <RBACPermissionsContext.Provider
      value={{
        userRoles,
        getRoles,
        hasRole,
        hasAdminRights,
        hasOperatorRights,
        hasReaderRights,
      }}
    >
      {children}
    </RBACPermissionsContext.Provider>
  )
}
